import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Container,
  Field,
  Flex,
  Heading,
  IconButton,
  Input,
  Spinner,
  Table,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import {
  FiChevronDown,
  FiChevronRight,
  FiEdit2,
  FiPlus,
  FiTrash2,
} from "react-icons/fi"

import {
  BudgetCategorys,
  BudgetCategoryTypes,
  PurchaseCatalog,
  ServiceCatalog,
  type BudgetCategoryPublic,
  type BudgetCategoryTypePublic,
  type BudgetSubCategoryPublic,
  type PurchaseItemPublic,
} from "@/client"
import useAuth from "@/hooks/useAuth"
import { handleError } from "@/utils"
import type { AxiosError as ApiError } from "axios"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"

export const Route = createFileRoute("/_layout/provider/purchasecatalog")({
  component: PurchaseCatalogPage,
})

// ── Edit purchase item dialog ─────────────────────────────────────────────────

const EditPurchaseItemDialog = ({
  item,
  trigger,
}: {
  item: PurchaseItemPublic
  trigger: React.ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const { register, handleSubmit } = useForm({
    defaultValues: {
      name: item.name,
      description: item.description ?? "",
      coContributionApplies: item.coContributionApplies,
    },
  })

  const mutation = useMutation({
    mutationFn: (data: { name: string; description: string; coContributionApplies: boolean }) =>
      PurchaseCatalog.updatePurchaseItem({
        path: { purchaseItemId: item.id },
        body: { ...data, description: data.description || null },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchaseItems", item.budgetSubCategoryId] })
      setOpen(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit Purchase Item</DialogTitle></DialogHeader>
        <DialogBody>
          <form
            id="edit-purchase-item-form"
            onSubmit={handleSubmit((d) =>
              mutation.mutate({ ...d, coContributionApplies: Boolean(d.coContributionApplies) })
            )}
          >
            <VStack gap={3}>
              <Field.Root>
                <Field.Label>Name</Field.Label>
                <Input {...register("name", { required: true })} />
              </Field.Root>
              <Field.Root>
                <Field.Label>Description</Field.Label>
                <Textarea {...register("description")} rows={3} />
              </Field.Root>
              <Checkbox.Root defaultChecked={item.coContributionApplies} {...register("coContributionApplies")}>
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>Co-contribution applies</Checkbox.Label>
              </Checkbox.Root>
            </VStack>
          </form>
        </DialogBody>
        <DialogFooter>
          <DialogCloseTrigger asChild><Button variant="ghost">Cancel</Button></DialogCloseTrigger>
          <Button type="submit" form="edit-purchase-item-form" loading={mutation.isPending} colorPalette="blue">Save</Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

// ── Purchase item row ─────────────────────────────────────────────────────────

const PurchaseItemRow = ({
  item,
  isSuperuser,
  isGlobal,
  onDelete,
}: {
  item: PurchaseItemPublic
  isSuperuser: boolean
  isGlobal: boolean
  onDelete: () => void
}) => {
  const deleteMutation = useMutation({
    mutationFn: () => PurchaseCatalog.deletePurchaseItem({ path: { purchaseItemId: item.id } }),
    onSuccess: onDelete,
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Table.Row _hover={{ bg: "gray.50" }}>
      <Table.Cell>
        <Text fontSize="sm" fontWeight="medium">{item.name}</Text>
        {item.description && (
          <Text fontSize="xs" color="gray.500" mt={0.5}>{item.description}</Text>
        )}
      </Table.Cell>
      <Table.Cell>
        <Badge colorPalette={item.coContributionApplies ? "green" : "gray"} size="sm">
          {item.coContributionApplies ? "Yes" : "No"}
        </Badge>
      </Table.Cell>
      <Table.Cell textAlign="right">
        {(!isGlobal || isSuperuser) && (
          <Flex gap={1} justify="flex-end">
            <EditPurchaseItemDialog
              item={item}
              trigger={
                <IconButton size="xs" variant="ghost" aria-label="edit">
                  <FiEdit2 />
                </IconButton>
              }
            />
            <IconButton
              size="xs"
              variant="ghost"
              colorPalette="red"
              aria-label="delete"
              loading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              <FiTrash2 />
            </IconButton>
          </Flex>
        )}
      </Table.Cell>
    </Table.Row>
  )
}

// ── Add purchase item form ────────────────────────────────────────────────────

const AddPurchaseItemForm = ({
  budgetSubCategoryId,
  onAdded,
}: {
  budgetSubCategoryId: string
  onAdded: () => void
}) => {
  const { register, handleSubmit, reset, watch } = useForm<{
    name: string
    description: string
    coContributionApplies: boolean
  }>({ defaultValues: { coContributionApplies: true } })

  const coApplies = watch("coContributionApplies")

  const mutation = useMutation({
    mutationFn: (data: { name: string; description: string; coContributionApplies: boolean }) =>
      PurchaseCatalog.createPurchaseItem({
        body: {
          budgetSubCategoryId,
          name: data.name,
          description: data.description || null,
          coContributionApplies: data.coContributionApplies,
        },
      }),
    onSuccess: () => { reset({ coContributionApplies: true }); onAdded() },
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Box as="form" onSubmit={handleSubmit((d) => mutation.mutate(d))} mt={2}>
      <Flex gap={2} align="flex-end" flexWrap="wrap">
        <Field.Root flex={2} minW="160px">
          <Field.Label fontSize="xs">Item name</Field.Label>
          <Input size="sm" placeholder="e.g. Walking Frame" {...register("name", { required: true })} />
        </Field.Root>
        <Field.Root flex={3} minW="200px">
          <Field.Label fontSize="xs">Description (optional)</Field.Label>
          <Input size="sm" placeholder="Short description" {...register("description")} />
        </Field.Root>
        <Flex align="center" gap={2} pb={1}>
          <Checkbox.Root
            checked={coApplies}
            onCheckedChange={(e) =>
              reset((prev) => ({ ...prev, coContributionApplies: !!e.checked }))
            }
          >
            <Checkbox.HiddenInput {...register("coContributionApplies")} />
            <Checkbox.Control />
            <Checkbox.Label fontSize="sm">Co-contribution applies</Checkbox.Label>
          </Checkbox.Root>
        </Flex>
        <Button size="sm" type="submit" colorPalette="green" loading={mutation.isPending}>
          <FiPlus /> Add
        </Button>
      </Flex>
    </Box>
  )
}

// ── Sub-category panel ────────────────────────────────────────────────────────

const SubCategoryPanel = ({
  sub,
  isSuperuser,
  isGlobal,
  onSubDeleted,
}: {
  sub: BudgetSubCategoryPublic
  isSuperuser: boolean
  isGlobal: boolean
  onSubDeleted: () => void
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editName, setEditName] = useState(sub.name)
  const queryClient = useQueryClient()

  const { data: itemsData, isLoading } = useQuery({
    queryKey: ["purchaseItems", sub.id],
    queryFn: () => PurchaseCatalog.listPurchaseItemsBySubcategory({ path: { subCategoryId: sub.id } }),
  })
  const items = itemsData?.data ?? []

  const updateMutation = useMutation({
    mutationFn: () => ServiceCatalog.updateSubcategory({ path: { subCategoryId: sub.id }, body: { name: editName } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["purchaseBudgetSubCategories"] })
      setEditing(false)
    },
    onError: (err: ApiError) => handleError(err),
  })

  const deleteMutation = useMutation({
    mutationFn: () => ServiceCatalog.deleteSubcategory({ path: { subCategoryId: sub.id } }),
    onSuccess: onSubDeleted,
    onError: (err: ApiError) => handleError(err),
  })

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden" mb={3}>
      <Flex
        px={3} py={2}
        bg="gray.50"
        align="center"
        justify="space-between"
        borderBottomWidth={collapsed ? "0" : "1px"}
        cursor="pointer"
        onClick={() => !editing && setCollapsed(!collapsed)}
      >
        <Flex align="center" gap={2}>
          {collapsed ? <FiChevronRight size={14} /> : <FiChevronDown size={14} />}
          {editing ? (
            <Flex gap={2} align="center" onClick={(e) => e.stopPropagation()}>
              <Input size="xs" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
              <Button size="xs" colorPalette="blue" loading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>Save</Button>
              <Button size="xs" variant="ghost" onClick={() => { setEditing(false); setEditName(sub.name) }}>Cancel</Button>
            </Flex>
          ) : (
            <Text fontWeight="semibold" fontSize="sm">{sub.name}</Text>
          )}
          <Badge size="sm" colorPalette="gray">{items.length} item{items.length !== 1 ? "s" : ""}</Badge>
        </Flex>
        {(!isGlobal || isSuperuser) && (
          <Flex gap={1} onClick={(e) => e.stopPropagation()}>
            <IconButton size="xs" variant="ghost" aria-label="edit sub" onClick={() => setEditing(true)}><FiEdit2 /></IconButton>
            <IconButton size="xs" variant="ghost" colorPalette="red" aria-label="delete sub" loading={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}><FiTrash2 /></IconButton>
          </Flex>
        )}
      </Flex>

      {!collapsed && (
        <Box px={3} py={2}>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Item</Table.ColumnHeader>
                  <Table.ColumnHeader>Co-contribution</Table.ColumnHeader>
                  <Table.ColumnHeader />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.map((item) => (
                  <PurchaseItemRow
                    key={item.id}
                    item={item}
                    isSuperuser={isSuperuser}
                    isGlobal={isGlobal}
                    onDelete={() => queryClient.invalidateQueries({ queryKey: ["purchaseItems", sub.id] })}
                  />
                ))}
              </Table.Body>
            </Table.Root>
          )}
          {(!isGlobal || isSuperuser) && (
            <AddPurchaseItemForm
              budgetSubCategoryId={sub.id}
              onAdded={() => queryClient.invalidateQueries({ queryKey: ["purchaseItems", sub.id] })}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

// ── Budget category section ───────────────────────────────────────────────────

const BudgetCategorySection = ({
  category,
  providerId,
  isSuperuser,
  isGlobal,
}: {
  category: BudgetCategoryPublic
  providerId: string
  isSuperuser: boolean
  isGlobal: boolean
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const queryClient = useQueryClient()

  const { data: subsData, isLoading } = useQuery({
    queryKey: ["purchaseBudgetSubCategories", providerId, category.id],
    queryFn: () => ServiceCatalog.listSubcategoriesByProvider({ path: { providerId }, query: { budgetCategoryId: category.id, subCategoryType: "purchase" } }),
  })
  const subs = subsData?.data ?? []

  return (
    <Box mb={6}>
      <Flex
        align="center"
        justify="space-between"
        mb={3}
        cursor="pointer"
        onClick={() => setCollapsed(!collapsed)}
      >
        <Flex align="center" gap={2}>
          {collapsed ? <FiChevronRight /> : <FiChevronDown />}
          <Heading size="sm">{category.category}</Heading>
          <Badge colorPalette="orange" size="sm">{subs.length} sub-categor{subs.length !== 1 ? "ies" : "y"}</Badge>
        </Flex>
      </Flex>

      {!collapsed && (
        <>
          {isLoading ? (
            <Spinner size="sm" />
          ) : (
            subs.map((sub) => (
              <SubCategoryPanel
                key={sub.id}
                sub={sub}
                isSuperuser={isSuperuser}
                isGlobal={isGlobal}
                onSubDeleted={() => queryClient.invalidateQueries({ queryKey: ["purchaseBudgetSubCategories", providerId, category.id] })}
              />
            ))
          )}
        </>
      )}
    </Box>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

function PurchaseCatalogPage() {
  const providerId = sessionStorage.getItem("selectedProviderId") ?? ""
  const providerName = sessionStorage.getItem("selectedProviderName") ?? "Provider"
  const { user } = useAuth()
  const isSuperuser = user?.is_superuser ?? false
  const [selectedTypeId, setSelectedTypeId] = useState<string>("")

  const { data: typesData } = useQuery({
    queryKey: ["budgetCategoryTypes"],
    queryFn: () => BudgetCategoryTypes.listBudgetCategoryTypes(),
  })
  const types: BudgetCategoryTypePublic[] = typesData?.data ?? []

  const { data: categoriesData, isLoading: loadingCats } = useQuery({
    queryKey: ["budgetCategories", selectedTypeId],
    queryFn: () => BudgetCategorys.readBudgetCategorys(),
    select: (d) => ({
      ...d,
      data: selectedTypeId
        ? d.data.filter((c) => c.budgetCategoryTypeId === selectedTypeId)
        : d.data,
    }),
  })
  const categories: BudgetCategoryPublic[] = categoriesData?.data ?? []

  return (
    <Container maxW="full">
      <Box pt={12} mb={6}>
        <Heading size="lg" mb={1}>{providerName} — Purchase Catalog</Heading>
        <Text color="gray.500" fontSize="sm">
          Manage purchase items (equipment, supplies, etc.) under budget sub-categories.
        </Text>
      </Box>

      {!providerId && <Text color="red.500">No provider selected.</Text>}

      {/* Budget Category Type filter */}
      <Box mb={6}>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Filter by Category Type</Text>
        <Flex gap={2} flexWrap="wrap">
          <Button
            size="sm"
            variant={selectedTypeId === "" ? "solid" : "outline"}
            colorPalette="gray"
            onClick={() => setSelectedTypeId("")}
          >
            All
          </Button>
          {types.map((t) => (
            <Button
              key={t.id}
              size="sm"
              variant={selectedTypeId === t.id ? "solid" : "outline"}
              colorPalette="orange"
              onClick={() => setSelectedTypeId(t.id)}
            >
              {t.categoryType}
            </Button>
          ))}
        </Flex>
      </Box>

      {loadingCats && <Spinner />}

      {categories.length === 0 && !loadingCats && (
        <Text color="gray.400">No budget categories found{selectedTypeId ? " for this type" : ""}.</Text>
      )}

      {categories.map((cat) => {
        const catType = types.find((t) => t.id === cat.budgetCategoryTypeId)
        return (
          <BudgetCategorySection
            key={cat.id}
            category={cat}
            providerId={providerId}
            isSuperuser={isSuperuser}
            isGlobal={catType?.isGlobal ?? false}
          />
        )
      })}
    </Container>
  )
}
